import type { ChiaveRequisito, VerdettoRilascio } from "../server/domain/deploy/requisiti.ts";
import type { Traduttore } from "./index.ts";

/**
 * Il verdetto sul rilascio senza interruzione messo in parole.
 *
 * Un punto solo per server e interfaccia: il rifiuto che la CI riceve e il
 * riquadro della scheda Rilascio dicono le stesse cose con le stesse frasi,
 * ciascuno nella sua lingua.
 */

export interface VerdettoDescritto {
  readonly pronto: boolean;
  readonly titolo: string;
  readonly effetto: string;
  readonly requisiti: readonly {
    readonly chiave: ChiaveRequisito;
    readonly soddisfatto: boolean;
    readonly titolo: string;
    readonly comeSoddisfarlo: string;
  }[];
  readonly avvertimenti: readonly { readonly chiave: string; readonly titolo: string; readonly dettaglio: string }[];
}

type Requisito = VerdettoRilascio["requisiti"][number];

function comeSoddisfarlo(r: Requisito, t: Traduttore): string {
  const servizi = r.servizi;
  switch (r.chiave) {
    case "strategia":
      return t.t("rilascio.requisito.strategia.come");
    case "instradamento":
      return t.t("rilascio.requisito.instradamento.come");
    case "proxy":
      if (r.proxy?.stato === "assente") return t.t("rilascio.requisito.proxy.assente");
      if (r.proxy?.stato === "ok") return t.t("rilascio.requisito.proxy.ok");
      return r.proxy?.motivo ?? t.t("rilascio.requisito.proxy.muto");
    case "fissi-dati":
      return servizi.length === 0 || r.progetto === undefined
        ? t.t("rilascio.requisito.fissi-dati.ok")
        : t.t("rilascio.requisito.fissi-dati.mancante", { servizi, progetto: r.progetto });
    default:
      return servizi.length === 0
        ? t.t(`rilascio.requisito.${r.chiave}.ok` as const)
        : t.t(`rilascio.requisito.${r.chiave}.mancante` as const, { servizi });
  }
}

export function descriviVerdetto(v: VerdettoRilascio, t: Traduttore): VerdettoDescritto {
  const mancanti = v.requisiti.filter((r) => !r.soddisfatto);

  return {
    pronto: v.pronto,
    titolo: v.pronto
      ? t.t("rilascio.verdetto.attivo")
      : mancanti.length === 1
        ? t.t("rilascio.verdetto.unaMancante", { cosa: t.t(`rilascio.mancanza.${mancanti[0]!.chiave}` as const) })
        : t.t("rilascio.verdetto.mancano", { n: mancanti.length, totale: v.requisiti.length }),
    effetto: t.t(v.pronto ? "rilascio.effetto.senzaInterruzione" : "rilascio.effetto.ricreazione"),
    requisiti: v.requisiti.map((r) => ({
      chiave: r.chiave,
      soddisfatto: r.soddisfatto,
      titolo: t.t(`rilascio.requisito.${r.chiave}.titolo` as const),
      comeSoddisfarlo: comeSoddisfarlo(r, t),
    })),
    avvertimenti: v.avvertimenti.map((a) => ({
      chiave: a.chiave,
      titolo: t.t(`rilascio.avvertimento.${a.chiave}.titolo` as const, { servizi: a.servizi }),
      dettaglio: t.t(`rilascio.avvertimento.${a.chiave}.dettaglio` as const),
    })),
  };
}
